/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import {
  first,
  forEach,
  isEmpty,
  isNumber,
  isNull,
  isUndefined,
  last,
  map,
  maxBy,
  minBy,
  pick,
  sortBy,
  sumBy,
  reduce,
  isNil,
  divide
} from 'lodash';
import moment from 'moment';
import numeral from 'numeral';

export const isPositiveValue = (val) => numeral(val).value() > 0;
export const isNegativeValue = (val) => numeral(val).value() < 0;

export const totalBalance = (accounts) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return 0;
  }
  return sumBy(accounts, (account) => {
    return account.balanceValue || 0;
  });
};

export const totalPreviousBalance = (accounts) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return 0;
  }
  return sumBy(accounts, (account) => {
    return account.previousBalanceValue || 0;
  });
};

export const calculateNetDollarAmount = (v1, v2) => {
  return isNumber(v1) && isNumber(v2) ? v1 * v2 : 0;
};

/**
 * Round a number to a specified precision
 * @param {number} num
 * @param {number=4} precision
 * @returns {number}
 */
export const reasonableRound = (num, precision = 4) =>
  parseFloat(num.toFixed(precision));

/**
 * Confirm that the value is a number and not insignificantly small i.e. 0.0000000000001, 1e-13
 * @param {number} num
 * @param {number=4} precision
 * @returns {boolean}
 */
export const isReasonableNumber = (num, precision = 4) =>
  !(Number.isNaN(num) || reasonableRound(num, precision) === 0);

/**
 * Returns what percent the new value is of the old value.
 * If the result is invalid or less than 0.0001 it will return 0
 * @param {number} newValue
 * @param {number} oldValue
 * @returns {number}
 */
export const percentChange = (newValue, oldValue) => {
  if (!isNumber(oldValue) || oldValue === 0) {
    return 0;
  }

  const percent = (newValue - oldValue) / Math.abs(oldValue);
  return isReasonableNumber(percent) ? percent : 0;
};

/**
 * Computes the percent of a value out of reference (or total) in terms of 1.
 *  ex: return 0.97 for 97%
 * @param {number} number
 * @param {number} reference
 * @param {number=0} precision
 * @returns {number} percent of value/reference if reference doesn't equal 0
 *                   0 if reference equals 0
 */
export const percentValueOf = (value, reference, precision = 0) => {
  if (reference === 0) {
    return 0;
  }

  return reasonableRound(value / reference, precision);
};

export const adjustPercent = (percent) =>
  isNumber(percent) ? percent / 100 : null;

export const difference = (newValue, oldValue) =>
  isReasonableNumber(newValue - oldValue, 3) ? newValue - oldValue : 0;

export const calculatePreviousBalance = (mV1, diff) => difference(mV1, diff);

export const calculateRawPositionMarketValue = (positions) =>
  sumBy(positions, (position) => {
    const { currentBalance } = position;
    const value = currentBalance ? currentBalance : 0;
    return isNumber(value) ? value : 0;
  });

export const calculatePositionsTotalDifference = (positions) =>
  sumBy(positions, (position) => {
    const { dailyChange } = position;
    return dailyChange &&
      dailyChange.difference &&
      isNumber(dailyChange.difference)
      ? dailyChange.difference
      : 0;
  });

export const maxAsOfDate = (accounts) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return null;
  }
  return maxBy(accounts, (account) => {
    return moment.utc(account.balanceAsOfDate);
  }).balanceAsOfDate;
};

export const intradayMaxAsOfDate = (accounts) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return null;
  }
  const maxDate = [];
  accounts.forEach((account) => {
    account.positions.forEach((position) => {
      maxDate.push(new Date(position.currentBalanceAsOfDate));
    });
  });
  return new Date(Math.max(...maxDate));
};

export const minInceptionDate = (accounts = []) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return null;
  }
  return minBy(accounts, (account) => moment.utc(account.inceptionDate))
    .inceptionDate;
};

export const minBeginDate = (history) => {
  if (isUndefined(history) || isEmpty(history)) {
    return null;
  }
  return minBy(history, (account) => moment.utc(account.beginDate)).beginDate;
};

export const dailyChangeAmount = (accounts) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return 0;
  }
  return sumBy(accounts, (account) => {
    return (account.balanceValue || 0) - (account.previousBalanceValue || 0);
  });
};

export const dailyChangePercentForAccount = (account) => {
  return percentChange(account.balanceValue, account.previousBalanceValue);
};

/*
 Beware this assumes you have previousBalanceValue and balanceValue on all accounts data
 */
export const dailyChangeRate = (accounts) => {
  if (isUndefined(accounts) || isEmpty(accounts)) {
    return 0;
  }
  const total = totalBalance(accounts);
  const daily = dailyChangeAmount(accounts);
  return percentChange(total, total - daily);
};

const initialSummary = {
  beginValue: 0.0,
  beginningMarketValue: 0.0,
  endValue: 0.0,
  endingMarketValue: 0.0,
  additions: 0.0,
  deductions: 0.0,
  income: 0.0,
  fluctuation: 0.0,
  marketFluctuation: 0.0,
  numOfAdditions: 0,
  numOfDeductions: 0,
  adjustments: 0
};

export const aggregateValues = (summary) => {
  return pick(
    summary,
    'beginningMarketValue',
    'endingMarketValue',
    'additions',
    'deductions',
    'income',
    'marketFluctuation',
    'adjustments'
  );
};

export const sumAggregates = (aggregates) => {
  let sum = aggregateValues(initialSummary);
  aggregates.forEach((aggregate) => {
    sum.beginningMarketValue += aggregate.beginningMarketValue;
    sum.endingMarketValue += aggregate.endingMarketValue;
    sum.additions += aggregate.additions;
    sum.deductions += aggregate.deductions;
    sum.income += aggregate.income;
    sum.marketFluctuation += aggregate.marketFluctuation;
    sum.adjustments += aggregate.adjustments;
  });

  return sum;
};

export const aggregateGrowth = (history) => {
  if (isUndefined(history) || isNull(history)) {
    return [];
  }

  const extracted = map(history, (h) => {
    return pick(h, 'date', 'marketValue', 'netInvestment');
  });

  return sortBy(extracted, 'date');
};

export const aggregateMetrics = (history) => {
  const metrics = {
    numOfAdditions: 0,
    numOfDeductions: 0,
    numOfIncome: 0
  };
  forEach(history, (h) => {
    metrics.numOfAdditions += h.numOfAdditions || 0;
    metrics.numOfDeductions += h.numOfDeductions || 0;
    metrics.numOfIncome += h.numOfIncome || 0;
  });
  return metrics;
};

// --- Growth Functions ---

const growthYears = (history) => {
  const begin = moment.utc(first(history).date);
  const end = moment.utc(last(history).date);
  return end.diff(begin, 'years', true);
};

export const growthTickFormat = (history) => {
  return growthYears(history) <= 1 ? '%b' : '%Y';
};

const middleOfMonth = (result, d) => {
  const mom = moment.utc(d.date);
  if (mom.date() === 15) {
    result.push(mom.toDate());
  }
  return result;
};

const middleOfYear = (result, d) => {
  const moy = moment.utc(d.date);
  if (moy.dayOfYear() === 183) {
    result.push(moy.toDate());
  }
  return result;
};

const everyTwoYears = (result, d) => {
  const ety = moment.utc(d.date);
  if (ety.dayOfYear() === 183 && ety.year() % 2 === 0) {
    result.push(ety.toDate());
  }
  return result;
};

const everyFourYears = (result, d) => {
  const ety = moment.utc(d.date);
  if (ety.dayOfYear() === 183 && ety.year() % 4 === 0) {
    result.push(ety.toDate());
  }
  return result;
};

export const growthTickValues = (history) => {
  const years = growthYears(history);
  let reduceFn = middleOfMonth;
  if (years > 1 && years <= 5) {
    reduceFn = middleOfYear;
  } else if (years > 5 && years <= 15) {
    reduceFn = everyTwoYears;
  } else if (years > 15) {
    reduceFn = everyFourYears;
  }
  return reduce(history, reduceFn, []);
};

export const getAge = (birthDate) => {
  return isNil(birthDate)
    ? 0
    : moment.utc().diff(moment.utc(birthDate), 'years');
};

// round up to the nearest power of ten if decimal is off by 0.01, 0.001, and so on.
export const roundUp = (number) => {
  let precision = number % 1 !== 0 ? (number + '').split('.')[1].length : 0;
  let powerOfTen = Math.pow(10, precision);

  return number.toString().match(/^9+.9+$/)
    ? number + divide(1.0, powerOfTen)
    : number;
};
